import { WebRequest } from "@lib/titan2/dist/api/core/request/WebRequest";
import { FactoryManager } from "@lib/titan2/dist/game/factory/FactoryManager";
import { ObjectUtil } from "@lib/titan2/dist/util/object/ObjectUtil";
import { Parser } from "./Parser";
import { ReplayParser } from "./replay/ReplayParser";
/**
 * Service.ts
 */
export class Service {
    constructor() {
        this.parser = null;
        this.replayParserClass = ReplayParser;
        this.state = ServiceState.DEFAULT;
    }
    /**
     * Gets/creates the `Service` singleton
     */
    static getInstance() {
        if (!Service.instance) {
            Service.instance = new Service();
        }
        return Service.instance;
    }
    /**
     * Given the name of a request, returns the appropriate response as a JSON
     * @param key - request key
     * @param data - JSON request data
     */
    static getResponse(key, data) {
        return Service.getInstance()._getResponse(key, data);
    }
    /**
     * Overwrites the default replay parser class if required
     * @param type - replay parser class. Must be a child class of `Parser`
     */
    static setReplayParserClass(type) {
        Service.getInstance()._setReplayParserClass(type);
    }
    /**
     * Resets ServiceState to `DEFAULT` and removes response parser
     */
    static reset() {
        Service.getInstance()._reset();
    }
    /**
     * Switches ServiceState to `REPLAY` and parses replayData
     * @param replayData - data returned from getData request
     */
    static startReplay(replayData) {
        Service.getInstance()._startReplay(replayData);
    }
    /**
     * Switches ServiceState to `MOCK_DATA` and sets parser.
     * @param parser - Parser to use. Must be able to return all required responses for mock game.
     */
    static startMockData(parser) {
        Service.getInstance()._startMockData(parser);
    }
    /**
     * @returns `true` when replay is in progress
     */
    static isReplay() {
        return Service.getInstance()._isReplay();
    }
    /**
     * @returns `true` when replay is in progress, but stopping soon
     */
    static isReplayStopping() {
        return Service.getInstance()._isReplayStopping();
    }
    /**
     * @returns `true` when all available replay `SPIN` requests have been requested
     */
    static isReplaySpinsComplete() {
        return Service.getInstance()._isReplaySpinsComplete();
    }
    /**
     * @returns `true` when all available replay `STAKE_TO_SPIN` requests have been requested
     */
    static isReplayStakeToSpinsComplete() {
        return Service.getInstance()._isReplayStakeToSpinsComplete();
    }
    /**
     * Sets state to `REPLAY_STOP` to prepare for stopping the replay
     */
    static stopReplay() {
        Service.getInstance()._stopReplay();
    }
    /**
     * @returns Current state of Service singleton
     */
    static getState() {
        return Service.getInstance()._getState();
    }
    /**
     * @returns The current response parser, or `null` if in default state (i.e. sending web requests)
     */
    static getCurrentParser() {
        return Service.getInstance().parser;
    }
    //////////////////////////////////////////////////////////////
    //////////////////////////////////////////////////////////////
    _setReplayParserClass(type) {
        if (type.prototype instanceof Parser) {
            this.replayParserClass = type;
        }
        else {
            throw new Error(`Service.ts: Cannot setReplayParserClass - Type "${type.name}" is not a Parser!`);
        }
    }
    _reset() {
        const prevState = this.state;
        this.state = ServiceState.DEFAULT;
        // If we were in a replay state, dispatch KPI to indicate the end of the replay
        if (prevState.isReplay() || prevState.isReplayStopping()) {
            const model = FactoryManager.getInstance().getFactory().getModel();
            model.setAnalyticsEnabled(true);
            model.dispatchKPI("SLINGO_STATE", "GAME_REPLAY_WATCH_ENDED");
        }
        this.parser = null;
    }
    _getResponse(key, data) {
        if (this.parser?.hasResponse(key)) {
            return this.parser.getResponse(key, data);
        }
        throw new Error("Service.ts: unknown request key: " + key);
    }
    _startReplay(replayData) {
        const model = FactoryManager.getInstance().getFactory().getModel();
        model.dispatchKPI("SLINGO_STATE", "GAME_REPLAY_WATCH_STARTED");
        model.setAnalyticsEnabled(false);
        this.state = ServiceState.REPLAY;
        this.parser = ObjectUtil.create(this.replayParserClass, replayData);
    }
    _isReplay() {
        return this.state.isReplay() || this.state.isReplayStopping();
    }
    _isReplayStopping() {
        return this.state.isReplayStopping();
    }
    _isReplaySpinsComplete() {
        return this._isReplay() && !this.parser.hasResponse("Spin");
    }
    _isReplayStakeToSpinsComplete() {
        return this._isReplay() && !this.parser.hasResponse("StakeToSpin");
    }
    _stopReplay() {
        this.state = ServiceState.REPLAY_STOP;
    }
    _startMockData(parser) {
        this.state = ServiceState.MOCK_DATA;
        this.parser = parser;
    }
    _getState() {
        return this.state;
    }
}
/**
 * ServiceRequest.ts
 */
export class ServiceRequest extends WebRequest {
    constructor(url, requestData = null) {
        super(url, requestData);
        /**
         * Calls the request handler, substituting the parser response when Service is not in default state
         */
        this.handleRequestComplete = function () {
            if (Service.getState().isDefault()) {
                this.handleResponse(this.request.getJSON());
            }
            else {
                this.handleResponse(this.responseData);
            }
        };
    }
    /**
     * Returns a unique key describing this request. Slingo requests include: "PurchaseEntry", "Spin", "Collect", "ChooseCell" and "CurrentGame".
     */
    getName() {
        return "Default";
    }
    /**
     * Sends a normal WebRequest if Service state is standard. Otherwise, gets a response from the active Service parser.
     * @param url - request url
     * @param data - request payload data
     */
    sendRequest(url, data) {
        if (Service.getState().isDefault()) {
            super.sendRequest(url, data);
        }
        else {
            this.responseData = Service.getResponse(this.getName(), data);
            //Call handleRequestComplete asynchronously to best mimic sending a real HttpRequest
            setTimeout(() => {
                this.handleRequestComplete();
            }, 1);
        }
    }
}
/**
 * ServiceState.ts
 */
export class ServiceState {
    constructor(type) {
        this.type = type;
    }
    isDefault() {
        return this === ServiceState.DEFAULT;
    }
    isReplay() {
        return this === ServiceState.REPLAY;
    }
    isReplayStopping() {
        return this === ServiceState.REPLAY_STOP;
    }
    isMockData() {
        return this === ServiceState.MOCK_DATA;
    }
}
ServiceState.DEFAULT = new ServiceState("DEFAULT");
ServiceState.REPLAY = new ServiceState("REPLAY");
ServiceState.REPLAY_STOP = new ServiceState("REPLAY_STOP");
ServiceState.MOCK_DATA = new ServiceState("MOCK_DATA");
//# sourceMappingURL=Service.js.map